import classNames from 'classnames'
import { Size } from '@opallabs/gem'
import { Checkbox, Icon } from '@opallabs/lapidary'
import { type CalendarFilterMode, type CalendarFilterOption } from '../types'
import { ModeMark } from './ModeSelect'
import * as styles from './styles'

/** How much of what sits under a row was chosen, as ARIA says it. */
export type TriState = 'false' | 'mixed' | 'true'

/**
 * The box in front of a row that stands for others.
 *
 * Lapidary's `Checkbox` cannot show a part-chosen state, so every row that can
 * be part-chosen draws this instead — sized, cornered and weighted exactly like
 * the Checkbox on the rows beside it. The row itself carries the role and the
 * `aria-checked` that goes with it; this is only the picture of the state.
 */
export const TriStateBox: React.FC<{ state: TriState }> = ({ state }) => (
  <span
    aria-checked={state}
    aria-hidden={true}
    className={styles.selectors.selectAllBox}
  >
    {state === 'true' ? (
      <Icon
        ariaHidden={true}
        color="currentColor"
        glyph="gem-check"
        size={Size.SmallIcon}
      />
    ) : null}
  </span>
)
TriStateBox.displayName = 'Calendar: TriStateBox'

export type OptionMarksProps = Readonly<{
  /**
   * The mark in front of the option, when the caller has one. It is decorative:
   * a channel icon labels itself with the channel's name, and the row already
   * carries that name as its text.
   */
  icon?: React.ReactNode
  option: CalendarFilterOption
  /**
   * The row this option sits under, said quietly in front of it, where the
   * option is drawn away from that row: "Instagram › Acme US" in a search.
   */
  path?: string
}>

/** The path, the dot, the mark and the name of one option, in that order. */
export const OptionMarks: React.FC<OptionMarksProps> = ({
  icon,
  option,
  path,
}) => (
  <>
    {path === undefined ? null : (
      <span
        className={classNames(
          styles.selectors.text,
          styles.selectors.optionPath
        )}
      >
        {`${path} › `}
      </span>
    )}
    {option.color === undefined ? null : (
      <span
        className={styles.selectors.optionDot}
        style={{ backgroundColor: option.color }}
      />
    )}
    {icon === undefined || icon === null ? null : (
      <span aria-hidden={true}>{icon}</span>
    )}
    <span className={styles.selectors.text}>{option.label}</span>
  </>
)
OptionMarks.displayName = 'Calendar: OptionMarks'

export type OptionRowProps = Readonly<{
  categoryId: string
  /** Whether the category's list holds this value. */
  checked?: boolean
  icon?: React.ReactNode
  /**
   * The mode a tick here is read in, where the row is drawn away from the
   * control that sets it. A row that hides what it holds wears the minus.
   */
  mode?: CalendarFilterMode
  onToggle: () => void
  option: CalendarFilterOption
  /**
   * How much of the values under this one was chosen, for a row that stands for
   * others: a channel over its accounts, a label set over its labels.
   *
   * A row given one is drawn as a checkbox of its own rather than a lapidary
   * `Checkbox`, which cannot show a part-chosen state.
   */
  partChosenState?: TriState
  path?: string
}>

/**
 * One checkable value, wherever it is offered: in a leaf, or in a search.
 *
 * A box says the category holds this value. What holding it means — matching it
 * or hiding it — belongs to the category, and the mode control above the rows
 * says which.
 *
 * A row keeps the same shape whatever it is marked with: a person clicking a
 * row must not have the control under their finger replaced by another.
 */
export const OptionRow: React.FC<OptionRowProps> = ({
  categoryId,
  checked = false,
  icon,
  mode,
  onToggle,
  option,
  partChosenState,
  path,
}) => {
  const htmlId = `calendar-filter-${categoryId}-${option.id}`
  const marks = (
    <>
      <OptionMarks icon={icon} option={option} path={path} />
      {mode !== 'exclude' ? null : (
        <span aria-hidden={true} className={styles.selectors.optionMode}>
          <ModeMark mode={mode} />
        </span>
      )}
    </>
  )

  if (partChosenState !== undefined) {
    return (
      <button
        aria-checked={partChosenState}
        className={classNames(
          styles.selectors.option,
          styles.selectors.parentOption
        )}
        onClick={onToggle}
        role="checkbox"
        type="button"
      >
        <TriStateBox state={partChosenState} />
        {marks}
      </button>
    )
  }

  return (
    <label className={styles.selectors.option} htmlFor={htmlId}>
      <Checkbox checked={checked} htmlId={htmlId} onChange={onToggle} />
      {marks}
    </label>
  )
}
OptionRow.displayName = 'Calendar: OptionRow'
